<script setup lang="ts">
import CircleFalse from '@/assets/svg/CircleFalse.svg'
import CircleTrue from '@/assets/svg/CircleTrue.svg'
import { useDependency } from '@/core/ioc/UseDependency'
import { AbortReason } from '@/enums/AbortReason'
import { SortOrder } from '@/enums/CaseManagement/SortOrder'
import { ServicePermissions } from '@/enums/ServicePermissions'
import { TokenType } from '@/enums/TokenType'
import { getChargerRegistrationStatusIcon, getChargerRegistrationStatusLabel } from '@/helpers/ChargerHelper'
import { useDateFormatter } from '@/helpers/DateFormatter'
import { clonePointerEventWithSrcElement } from '@/helpers/HtmlHelper'
import { getSessionStateLabel, getSessionStateStatusImage } from '@/helpers/SessionHelper'
import { isClosed } from '@/helpers/WorkOrderHelper'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { IPaginationOptions } from '@/misc/IPaginationOptions'
import type { IAuthorisationFilterParams } from '@/models/Authorisation/Filtering/IAuthorisationFilterParams'
import type { IAuthorisation } from '@/models/Authorisation/IAuthorisation'
import type { ICaseManagement } from '@/models/CaseManagement/ICaseManagement'
import type { ICharger } from '@/models/ICharger'
import type { IChargingSite } from '@/models/IChargingSite'
import type { IContentPage } from '@/models/Pagination/IContentPage'
import type { IResultsPage } from '@/models/Pagination/IResultsPage'
import type { ISession } from '@/models/Sessions/ISession'
import type { IWorkOrderFilter } from '@/models/WorkOrder/Filtering/IWorkOrderFilter'
import type { IWorkOrder } from '@/models/WorkOrder/IWorkOrder'
import type IAuthorisationService from '@/service/authorisation/IAuthorisationService'
import type ICaseManagementService from '@/service/casemanagement/ICaseManagementService'
import type IChargerService from '@/service/charger/IChargerService'
import type IPermissionChecker from '@/service/permissions/IPermissionChecker'
import { SERVICES_TYPES } from '@/service/ServicesTypes'
import type ISessionService from '@/service/session/ISessionService'
import type IChargingSiteService from '@/service/site/IChargingSiteService'
import type IWorkOrderService from '@/service/workorder/IWorkOrderService'
import WorkOrderDetails from '@/views/pages/WorkOrder/WorkOrderDetails.vue'
import type { DataTableRowClickEvent } from 'primevue/datatable'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import SiteConnectorsStatus from '../Connectors/SiteConnectorsStatus.vue'

const { t, locale } = useI18n()
const { formatDateByLocale } = useDateFormatter()
const router = useRouter()
const permissionChecker = useDependency<IPermissionChecker>(SERVICES_TYPES.PermissionChecker)

const abortController = ref<AbortController | null>(null)
const paginationOptions: IPaginationOptions = { PageNumber: 1, PageSize: 10 }
const loading = ref(false)

const generalSearch = ref()
const searchOp = ref()
const modelRef = ref<string>('')
const enum SearchTypes {
  Chargers,
  Sites,
  Sessions,
  Authorisations,
  Cases,
  WorkOrders
}

const buildSearchOptions = () => {
  const result = []
  if (permissionChecker.canGo([ServicePermissions.ChargePointsPermission, ServicePermissions.ChargePointDetailsPermission])) {
    result.push({ name: t('generalSearch.search.chargers'), value: SearchTypes.Chargers })
  }
  if (permissionChecker.canGo([ServicePermissions.SiteMonitoringPermission, ServicePermissions.SiteDetailsPermission])) {
    result.push({ name: t('generalSearch.search.sites'), value: SearchTypes.Sites })
  }
  if (permissionChecker.canGo([ServicePermissions.SessionPermission, ServicePermissions.SessionDetailsPermission])) {
    result.push({ name: t('generalSearch.search.sessions'), value: SearchTypes.Sessions })
  }
  if (permissionChecker.canGo([ServicePermissions.AuthorisationsPermission, ServicePermissions.AuthorisationDetailsPermission])) {
    result.push({ name: t('generalSearch.search.authorisations'), value: SearchTypes.Authorisations })
  }
  if (permissionChecker.canGo([ServicePermissions.CaseManagementPermission, ServicePermissions.CaseManagementDetailsPermission])) {
    result.push({ name: t('generalSearch.search.cases'), value: SearchTypes.Cases })
  }
  if (permissionChecker.canGo([ServicePermissions.WorkOrderPermission, ServicePermissions.WorkOrderDetailsPermission])) {
    result.push({ name: t('generalSearch.search.workOrders'), value: SearchTypes.WorkOrders })
  }
  return result
}

const searchOptions = computed(() => buildSearchOptions())

const searchType = ref<SearchTypes>(SearchTypes.Chargers)

const getPlaceHolder = () => {
  switch (searchType.value) {
    case SearchTypes.Chargers:
      return t('generalSearch.chargers.placeHolder')
    case SearchTypes.Sites:
      return t('generalSearch.sites.placeHolder')
    case SearchTypes.Sessions:
      return t('generalSearch.sessions.placeHolder')
    case SearchTypes.Authorisations:
      return t('generalSearch.authorisations.placeHolder')
    case SearchTypes.Cases:
      return t('generalSearch.cases.placeHolder')
    case SearchTypes.WorkOrders:
      return t('generalSearch.workOrders.placeHolder')
    default:
      return ''
  }
}

const timeoutId = ref<ReturnType<typeof setTimeout>>()
const defaultTimeout: number = 500

function onScroll() {
  resizeOverlay()
}

function onResize() {
  resizeOverlay()
}

const resizeOverlay = async () => {
  if (searchOp.value && searchOp.value.visible && typeof searchOp.value.alignOverlay === 'function') {
    searchOp.value.alignOverlay()
  }
}

const handleClick = (event: MouseEvent) => {
  const el = generalSearch.value?.$el || generalSearch.value
  opEvent.value = clonePointerEventWithSrcElement(event, el)
  searchOp.value.show(opEvent.value)
}

const handleKeyUp = async () => {
  clearTimeout(timeoutId.value)
  timeoutId.value = setTimeout(async () => {
    if (modelRef.value && modelRef.value.length > 3) {
      refreshData()
      if (opEvent.value) {
        searchOp.value.show(opEvent.value)
        resizeOverlay()
      }
    } else {
      resetData()
    }
  }, defaultTimeout)
}

// #region Sites Search
const siteService = useDependency<IChargingSiteService>(SERVICES_TYPES.SiteType)
const sites = ref<IPaginatedResponse<IChargingSite> | null>(null)
const refreshSites = () => {
  loading.value = true
  abortController.value = resetAbortController()
  if (modelRef.value) {
    siteService
      .searchChargingSites(modelRef.value, paginationOptions.PageNumber, paginationOptions.PageSize, false, abortController.value)
      .then(data => {
        sites.value = data
      })
      .finally(() => {
        loading.value = false
        abortController.value = null
      })
  }
}
const siteClick = (event: DataTableRowClickEvent<IChargingSite>) => {
  if (event?.data.Address?.Code) {
    router.push({ name: 'sitedetails', params: { siteId: event.data.Address?.Code } })
    modelRef.value = ''
    resetData()
    searchOp.value.hide()
  }
}
const canShowSitesList = computed(() => modelRef.value && searchType.value === SearchTypes.Sites)
// #endregion

// #region Chargers Search
const chargerService = useDependency<IChargerService>(SERVICES_TYPES.ChargerService)
const chargers = ref<IPaginatedResponse<ICharger> | null>()

const refreshChargers = () => {
  loading.value = true
  abortController.value = resetAbortController()
  if (modelRef.value) {
    chargerService
      .searchChargers(modelRef.value, paginationOptions.PageNumber, paginationOptions.PageSize, abortController.value)
      .then(data => {
        chargers.value = data
      })
      .finally(() => {
        loading.value = false
        abortController.value = null
      })
  }
}
const canShowChargersList = computed(() => modelRef.value && searchType.value === SearchTypes.Chargers)
const chargerClick = (event: DataTableRowClickEvent<ICharger>) => {
  if (event?.data.ChargePointId) {
    router.push({ name: 'chargepointdetails', params: { chargePointId: event.data.ChargePointId } })
    modelRef.value = ''
    resetData()
    searchOp.value.hide()
  }
}
// #endregion

// #region Sessions Search
const sessionService = useDependency<ISessionService>(SERVICES_TYPES.SessionService)
const sessions = ref<IPaginatedResponse<ISession> | null>()

const refreshSessions = () => {
  if (!modelRef.value) {
    return
  }
  loading.value = true
  abortController.value = resetAbortController()
  let sessionQueryParamStr = `sortField=sessionStart&isAscending=false&pageSize=${paginationOptions.PageSize}&pageNumber=${paginationOptions.PageNumber - 1}`
  sessionQueryParamStr += Number.isNaN(Number(modelRef.value)) ? `&chargePointId=${modelRef.value.toUpperCase()}` : `&chargingSessionId=${modelRef.value}`
  sessionService
    .getSlicedSessions(sessionQueryParamStr, abortController.value)
    .then(data => (sessions.value = data))
    .finally(() => {
      loading.value = false
      abortController.value = null
    })
}

const canShowSessionsList = computed(() => modelRef.value && searchType.value === SearchTypes.Sessions)
const sessionClick = (event: DataTableRowClickEvent<ISession>) => {
  if (event?.data.SessionId) {
    router.push({ name: 'sessiondetails', params: { sessionId: event.data.SessionId } })
    modelRef.value = ''
    resetData()
    searchOp.value.hide()
  }
}
// #endregion

// #region Authorisation Search
const authorisationService = useDependency<IAuthorisationService>(SERVICES_TYPES.AuthorisationService)
const authorisations = ref<IContentPage<IAuthorisation> | null>()

const refreshAuthorisations = () => {
  if (!modelRef.value) {
    return
  }
  loading.value = true
  abortController.value = resetAbortController()
  const currentFilter: IAuthorisationFilterParams = {}
  if (Number.isNaN(Number(modelRef.value))) {
    currentFilter.ChargePointId = modelRef.value.toUpperCase()
  } else {
    currentFilter.AuthorisationReference = Number(modelRef.value)
  }
  authorisationService
    .getAuthorisations(paginationOptions, '', false, currentFilter, abortController.value)
    .then(data => (authorisations.value = data))
    .finally(() => {
      loading.value = false
      abortController.value = null
    })
}

const canShowAuthorisationsList = computed(() => modelRef.value && searchType.value === SearchTypes.Authorisations)
const authorisationClick = (event: DataTableRowClickEvent<IAuthorisation>) => {
  if (event?.data.AuthorisationReference) {
    router.push({ name: 'authorisationdetails', params: { authorisationId: event.data.AuthorisationReference } })
    modelRef.value = ''
    resetData()
    searchOp.value.hide()
  }
}
// #endregion

// #region Cases Search
const caseService = useDependency<ICaseManagementService>(SERVICES_TYPES.CaseManagementService)
const cases = ref<IPaginatedResponse<ICaseManagement> | null>()

const refreshCases = () => {
  if (!modelRef.value) {
    return
  }
  loading.value = true
  abortController.value = resetAbortController()
  caseService
    .getCasesByChargePoint(modelRef.value.toUpperCase(), paginationOptions.PageNumber, paginationOptions.PageSize, 'CreatedOn', false, '', '', '', false, undefined, undefined, abortController.value)
    .then(data => (cases.value = data))
    .finally(() => {
      loading.value = false
      abortController.value = null
    })
}

const canShowCasesList = computed(() => modelRef.value && searchType.value === SearchTypes.Cases)
const caseClick = (event: DataTableRowClickEvent<ICaseManagement>) => {
  if (event?.data.CaseNumber) {
    router.push({ name: 'casemanagementdetails', params: { caseNumber: event.data.CaseNumber } })
    modelRef.value = ''
    resetData()
    searchOp.value.hide()
  }
}
// #endregion

// #region Work Orders Search
const workOrderService = useDependency<IWorkOrderService>(SERVICES_TYPES.WorkOrderService)
const workOrders = ref<IResultsPage<IWorkOrder> | null>()

const refreshWorkOrders = () => {
  if (!modelRef.value) {
    return
  }
  loading.value = true
  abortController.value = resetAbortController()
  const workOrderFilter: IWorkOrderFilter = {
    PageIndex: paginationOptions.PageNumber,
    PageSize: paginationOptions.PageSize,
    WorkOrderNumber: modelRef.value,
    SortColumn: 'CreatedOn',
    SortOrder: SortOrder.Descending
  }
  workOrderService
    .getWorkOrders(workOrderFilter, abortController.value)
    .then(data => (workOrders.value = data))
    .finally(() => {
      loading.value = false
      abortController.value = null
    })
}
const selectedWorkOrder = ref<IWorkOrder>()
const workOrderDetailsVisible = ref(false)
const canShowWorkOrderList = computed(() => modelRef.value && searchType.value === SearchTypes.WorkOrders)
const workOrderClick = async (event: DataTableRowClickEvent<IWorkOrder>) => {
  if (event?.data.WorkOrderNumber) {
    selectedWorkOrder.value = await workOrderService.getWorkOrder(event.data.WorkOrderNumber)
    workOrderDetailsVisible.value = true
  }
}

const workOrderButtonClick = async (workOder: IWorkOrder) => {
  if (workOder.WorkOrderNumber) {
    selectedWorkOrder.value = await workOrderService.getWorkOrder(workOder.WorkOrderNumber)
    workOrderDetailsVisible.value = true
  }
}
// #endregion

const resetData = () => {
  if (abortController.value) {
    abortController.value.abort()
    abortController.value = null
  }
  sites.value = null
  chargers.value = null
  sessions.value = null
  authorisations.value = null
  cases.value = null
  workOrders.value = null
}

const opEvent = ref<MouseEvent | null>(null)

const clearSearch = () => {
  if (modelRef.value) {
    modelRef.value = ''
    handleKeyUp()
  }
}

const resetAbortController = (): AbortController => {
  if (abortController.value) {
    abortController.value.abort(AbortReason.Canceled)
  }
  return new AbortController()
}

watch(
  () => searchType.value,
  () => {
    if (modelRef.value && modelRef.value.length > 3) {
      refreshData()
    } else {
      resetData()
    }
  }
)

const refreshData = () => {
  switch (searchType.value) {
    case SearchTypes.Chargers:
      refreshChargers()
      break
    case SearchTypes.Sites:
      refreshSites()
      break
    case SearchTypes.Sessions:
      refreshSessions()
      break
    case SearchTypes.Authorisations:
      refreshAuthorisations()
      break
    case SearchTypes.Cases:
      refreshCases()
      break
    case SearchTypes.WorkOrders:
      refreshWorkOrders()
      break
    default:
      return
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <InputGroup>
    <InputText ref="generalSearch" id="generalSearch" v-model="modelRef" @click="handleClick($event)" @keyup="handleKeyUp()" :placeholder="getPlaceHolder()" class="w-full" data-cy="global-search" />
    <Button icon="pi pi-times" v-if="modelRef !== '' && !loading" @click="clearSearch()" severity="warning" data-cy="clear-search" />
    <InputGroupAddon v-if="loading">
      <InputIcon class="pi pi-spin pi-spinner" />
    </InputGroupAddon>
    <InputGroupAddon v-if="!loading && modelRef === ''">
      <InputIcon class="pi pi-search" />
    </InputGroupAddon>
  </InputGroup>

  <Popover ref="searchOp" :dismissable="true" :close-on-escape="true" class="flex flex-column w-[50%] h-[80%]">
    <template #default>
      <div>
        <SelectButton v-model="searchType" :options="searchOptions" option-label="name" option-value="value" :allow-empty="false" data-cy="generalsearch-searchtypes" />
      </div>
      <Divider />
      <DataTable
        v-if="canShowSitesList"
        :value="sites?.Values"
        lazy
        scrollable
        scrollHeight="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="siteClick($event)"
        data-cy="search-sites"
      >
        <Column :header="t('siteMonitoring.datatable.locationCode')" field="Address.Code" />
        <Column :header="t('siteMonitoring.datatable.location')" field="Address.Name" />
        <Column :header="t('siteMonitoring.datatable.status')" field="Connectors.Status">
          <template #body="slotProps">
            <SiteConnectorsStatus :key="slotProps.data.Id" :collapsed="true" :chargers="slotProps.data.Chargers" />
          </template>
        </Column>
        <template #empty>
          <div class="text-center p-4" data-cy="empty-sites">
            <i class="fa fa-map-location text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('generalSearch.sites.noSites') }}</p>
          </div>
        </template>
      </DataTable>
      <DataTable
        v-if="canShowChargersList"
        :value="chargers?.Values"
        lazy
        scrollable
        scroll-height="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="chargerClick($event)"
        data-cy="search-chargers"
      >
        <Column :header="t('siteDetail.columnTab.chargePointId')" field="ChargePointId" />
        <Column :header="t('siteDetail.columnTab.location')" field="ChargingSite.Address.Name" />
        <Column :header="t('generalSearch.chargers.connectors')">
          <template #body="slotProps">
            <SiteConnectorsStatus :key="slotProps.data.Id" :collapsed="true" :chargers="[slotProps.data]" />
          </template>
        </Column>
        <Column :header="t('siteDetail.columnTab.status')" field="Status">
          <template #body="slotProps">
            <div>
              <Image :src="getChargerRegistrationStatusIcon(slotProps.data.Status)" :title="getChargerRegistrationStatusLabel(slotProps.data.Status).toUpperCase()" />
            </div>
          </template>
        </Column>
        <template #empty>
          <div class="text-center p-4" data-cy="empty-chargers">
            <i class="fa fa-charging-station text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('generalSearch.chargers.noChargers') }}</p>
          </div>
        </template>
      </DataTable>
      <DataTable
        v-if="canShowSessionsList"
        :value="sessions?.Values"
        lazy
        scrollable
        scroll-height="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="sessionClick($event)"
        size="large"
        data-cy="search-sessions"
      >
        <Column :header="t('sessions.headers.sessionId')" field="SessionId" />
        <Column :header="t('sessions.headers.chargePointId')" field="ChargePointId">
          <template #body="slotProps">
            <div v-if="permissionChecker.canGo([ServicePermissions.ChargePointDetailsPermission])">
              <router-link :to="{ path: '/chargepointdetails/' + slotProps.data.ChargePointId }" class="cp-title-hyperlink font-semibold">
                {{ slotProps.data.ChargePointId }}
              </router-link>
            </div>
            <div v-else>
              {{ slotProps.data.ChargePointId }}
            </div>
          </template>
        </Column>
        <Column :header="t('sessions.headers.authorisationReference')" field="AuthorisationReference">
          <template #body="slotProps">
            <div v-if="permissionChecker.canGo([ServicePermissions.AuthorisationDetailsPermission])">
              <router-link :to="{ path: '/authorisationdetails/' + slotProps.data.AuthorisationReference }" class="cp-title-hyperlink font-semibold">
                {{ slotProps.data.AuthorisationReference }}
              </router-link>
            </div>
            <div v-else>
              {{ slotProps.data.AuthorisationReference }}
            </div>
          </template>
        </Column>
        <Column :header="t('sessions.headers.state')" field="State">
          <template #body="slotProps">
            <div class="flex align-content-center">
              <Image
                :src="getSessionStateStatusImage(slotProps.data.SessionState)"
                :data-cy="`session-state-${slotProps.data.SessionId}-state`"
                v-tooltip.left="{
                  value: `${t(`sessions.states.${getSessionStateLabel(slotProps.data.SessionState)}`)}`
                }"
              />
              <div class="ml-2 align-content-center">{{ t(`sessions.states.${getSessionStateLabel(slotProps.data.SessionState)}`) }}</div>
            </div>
          </template>
        </Column>
        <template #empty>
          <div class="text-center p-4" data-cy="empty-sessions">
            <i class="pi pi-bolt text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('sessions.nosessions') }}</p>
          </div>
        </template>
      </DataTable>
      <DataTable
        v-if="canShowAuthorisationsList"
        :value="authorisations?.Contents"
        lazy
        scrollable
        scroll-height="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="authorisationClick($event)"
        size="large"
        data-cy="search-authorisations"
      >
        <Column :header="t('authorisation.reference')" field="AuthorisationReference" />
        <Column :header="t('authorisation.chargePoint')" field="ChargePointId">
          <template #body="slotProps">
            <router-link
              v-if="slotProps.data.ChargePointId && permissionChecker.canShow([ServicePermissions.ChargePointDetailsPermission])"
              :to="{ path: '/chargepointdetails/' + slotProps.data.ChargePointId }"
              class="cp-title-hyperlink font-semibold"
              data-cy="authorisation-chargepointid"
            >
              {{ slotProps.data.ChargePointId }}
            </router-link>
            <span v-else class="text-md justify-content-center" data-cy="authorisation-chargepointId">{{ slotProps.data.ChargePointId }}</span>
          </template>
        </Column>
        <Column :header="t('authorisation.type')" field="TokenType">
          <template #body="slotProps">
            {{ TokenType[slotProps.data.TokenType]?.toUpperCase() }}
          </template>
        </Column>
        <Column :header="t('authorisation.authorised')" field="Authorised">
          <template #body="slotProps">
            <Image :src="slotProps.data.Authorised ? CircleTrue : CircleFalse" />
          </template>
        </Column>
        <template #empty>
          <div class="text-center p-4" data-cy="empty-authorisations">
            <i class="pi pi-key text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('authorisation.noAuthorisations') }}</p>
          </div>
        </template>
      </DataTable>
      <DataTable
        v-if="canShowCasesList"
        :value="cases?.Values"
        lazy
        scrollable
        scroll-height="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="caseClick($event)"
        size="large"
        data-cy="search-cases"
      >
        <Column :header="t('caseManagement.columns.title')" field="Title" />
        <Column :header="t('caseManagement.columns.customer')" field="CustomerName" />
        <Column :header="t('caseManagement.columns.chargePointId')" field="ChargePointId">
          <template #body="slotProps">
            <RouterLink
              v-if="permissionChecker.canGo([ServicePermissions.ChargePointDetailsPermission])"
              :to="{ path: '/chargepointdetails/' + slotProps.data.ChargePointId }"
              class="cp-title-hyperlink font-semibold"
              >{{ slotProps.data.ChargePointId }}</RouterLink
            >
            <span v-else>
              {{ slotProps.data.ChargePointId }}
            </span>
          </template>
        </Column>
        <Column :header="t('caseManagement.columns.createdOn')" field="CreatedOn">
          <template #body="slotProps">
            {{ formatDateByLocale(slotProps.data.CreatedOn, locale) }}
          </template>
        </Column>
        <template #empty>
          <div class="text-center p-4" data-cy="empty-cases">
            <i class="pi pi-inbox text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('caseManagement.noCases') }}</p>
          </div>
        </template>
      </DataTable>
      <DataTable
        v-if="canShowWorkOrderList"
        :value="workOrders?.Results"
        lazy
        scrollable
        scroll-height="flex"
        :rows="paginationOptions.PageSize"
        :loading="loading"
        :row-hover="true"
        :highlight-on-select="true"
        :show-gridlines="false"
        selection-mode="single"
        :show-headers="true"
        @row-click="workOrderClick($event)"
        size="large"
        data-cy="search-workorders"
      >
        <Column :header="t('workOrder.state')">
          <template #body="slotProps">
            <div v-if="isClosed(slotProps.data.Status)">
              <Tag :value="t('workOrder.closed')" severity="success" />
            </div>
            <div v-else>
              <Tag :value="t('workOrder.open')" severity="warn" />
            </div>
          </template>
        </Column>
        <Column field="WorkOrderNumber" :header="t('workOrder.workOrderNumber')">
          <template #body="slotProps">
            <div v-if="permissionChecker.canShow([ServicePermissions.WorkOrderDetailsPermission])">
              <Button :label="slotProps.data.WorkOrderNumber" variant="text" raised severity="primary" @click="workOrderButtonClick(slotProps.data)" />
            </div>
            <div v-else>
              {{ slotProps.data.WorkOrderNumber }}
            </div>
          </template>
        </Column>
        <Column field="ChargePointNumber" :header="t('workOrder.chargePoint')">
          <template #body="slotProps">
            <router-link
              v-if="permissionChecker.canGo([ServicePermissions.ChargePointDetailsPermission])"
              :to="{ path: '/chargepointdetails/' + slotProps.data.ChargePointNumber }"
              class="cp-title-hyperlink font-semibold"
            >
              {{ slotProps.data.ChargePointNumber }}
            </router-link>
            <div v-else>
              {{ slotProps.data.ChargePointNumber }}
            </div>
          </template>
        </Column>
        <Column field="CaseNumber" :header="t('workOrder.caseNumber')">
          <template #body="slotProps">
            <div v-if="permissionChecker.canShow([ServicePermissions.CaseManagementDetailsPermission])">
              <router-link :to="{ path: '/casemanagement/' + slotProps.data.CaseNumber }" class="cp-title-hyperlink font-semibold nowrap" data-cy="workorder-casemgt">
                {{ slotProps.data.CaseNumber }}
              </router-link>
            </div>
            <div v-else data-cy="workorder-casemgt-nolink">
              {{ slotProps.data.CaseNumber }}
            </div>
          </template>
        </Column>
        <Column field="Status" :header="t('workOrder.status')" />
        <template #empty>
          <div class="text-center p-4" data-cy="empty-work-orders">
            <i class="pi pi-file-check text-4xl text-400 mb-3"></i>
            <p class="text-600">{{ t('workOrder.noWorkOrders') }}</p>
          </div>
        </template>
      </DataTable>
      <Dialog v-model:visible="workOrderDetailsVisible" modal pt:mask:class="backdrop-blur-sm" pt:root:class="max-w-[60%] max-h-[50%] w-full" pt:content:class="h-full">
        <WorkOrderDetails :work-order="selectedWorkOrder" />
        <template #footer>
          <Button :label="t('general.close')" severity="secondary" @click="workOrderDetailsVisible = false" autofocus />
        </template>
      </Dialog>
    </template>
  </Popover>
</template>

<style lang="css" scoped>
:deep(.p-datatable-tbody > tr > td) {
  border: 0 !important;
}

:deep(.p-datatable-column-title) {
  white-space: nowrap !important;
}
</style>
